import * as React from "react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "./chart";
import { cn } from "@/lib/utils";

export interface PieChartProps {
  data: any[];
  className?: string;
  config?: any;
  variant?:
    | "default"
    | "animated"
    | "interactive"
    | "minimal"
    | "donut"
    | "gradient";
  theme?: "light" | "dark" | "minimal";
  responsive?: boolean;
  accessible?: boolean;
  colors?: string[];
  innerRadius?: number | string;
  outerRadius?: number | string;
  showLabels?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  height?: number | string;
  width?: number | string;
  dataKey?: string;
  nameKey?: string;
  valueKey?: string;
  labelFormatter?: (props: any) => string;
  paddingAngle?: number;
  startAngle?: number;
  endAngle?: number;
  minAngle?: number;
}

export function PieChart({
  data,
  className,
  config = {},
  variant = "default",
  theme = "light",
  responsive = true,
  accessible = true,
  colors = [
    "hsl(221.2 83.2% 53.3%)",
    "hsl(217.2 91.2% 59.8%)",
    "hsl(214.3 31.8% 91.4%)",
    "hsl(210 40% 96%)",
  ],
  innerRadius = 0,
  outerRadius = "80%",
  showLabels = true,
  showLegend = false,
  showTooltip = true,
  height,
  width,
  dataKey = "value",
  nameKey = "name",
  valueKey = "value",
  labelFormatter,
  paddingAngle = 0,
  startAngle = 0,
  endAngle = 360,
  minAngle = 0,
  ...props
}: PieChartProps) {
  const chartId = React.useId();

  // Generate gradient IDs
  const gradientIds = data.map(
    (_, index) => `pie-gradient-${chartId}-${index}`
  );

  // Determine colors based on theme
  const getThemeColors = () => {
    switch (theme) {
      case "dark":
        return ["#60a5fa", "#34d399", "#fbbf24", "#f87171"];
      case "minimal":
        return ["#6b7280", "#9ca3af", "#d1d5db", "#e5e7eb"];
      default:
        return colors;
    }
  };

  const themeColors = getThemeColors();

  // Determine variant-specific props
  const getVariantProps = () => {
    switch (variant) {
      case "animated":
        return {
          className: cn(className, "wl-pie-chart-animated"),
          animationDuration: 1500,
          animationEasing: "ease-out",
        };
      case "interactive":
        return {
          className: cn(className, "wl-pie-chart-interactive"),
        };
      case "minimal":
        return {
          className: cn(className, "wl-pie-chart-minimal"),
          showLabels: false,
        };
      case "donut":
        return {
          className: cn(className, "wl-pie-chart-donut"),
          innerRadius: "40%",
        };
      case "gradient":
        return {
          className: cn(className, "wl-pie-chart-gradient"),
        };
      default:
        return {
          className,
        };
    }
  };

  const variantProps = getVariantProps();

  // Get inner radius based on variant
  const getInnerRadius = () => {
    if (variant === "donut") return "40%";
    return innerRadius;
  };

  // Default label formatter
  const defaultLabelFormatter = (props: any) => {
    const { name, percent } = props;
    return `${name} ${(percent * 100).toFixed(0)}%`;
  };

  return (
    <div
      className={cn(
        "wl-pie-chart",
        "wl-pie-chart-theme",
        `wl-pie-chart-theme--${theme}`,
        variant !== "default" && `wl-pie-chart-${variant}`,
        responsive && "wl-pie-chart-responsive",
        accessible && "wl-pie-chart-accessible",
        className
      )}
      data-chart-type="pie"
      style={{ height, width }}
      {...props}
    >
      <ChartContainer config={config}>
        <RechartsPieChart
          margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
          {...variantProps}
        >
          {variant === "gradient" && (
            <defs>
              {data.map((_, index) => (
                <linearGradient
                  key={gradientIds[index]}
                  id={gradientIds[index]}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={themeColors[index % themeColors.length]}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={themeColors[index % themeColors.length]}
                    stopOpacity={0.3}
                  />
                </linearGradient>
              ))}
            </defs>
          )}

          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={
              showLabels ? labelFormatter || defaultLabelFormatter : undefined
            }
            outerRadius={outerRadius}
            innerRadius={getInnerRadius()}
            fill="#8884d8"
            dataKey={dataKey}
            nameKey={nameKey}
            valueKey={valueKey}
            paddingAngle={paddingAngle}
            startAngle={startAngle}
            endAngle={endAngle}
            minAngle={minAngle}
            animationDuration={variant === "animated" ? 1500 : undefined}
            animationEasing={variant === "animated" ? "ease-out" : undefined}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  variant === "gradient"
                    ? `url(#${gradientIds[index]})`
                    : themeColors[index % themeColors.length]
                }
              />
            ))}
          </Pie>

          {showTooltip && <ChartTooltip content={<ChartTooltipContent />} />}

          {showLegend && <ChartLegend content={<ChartLegendContent />} />}
        </RechartsPieChart>
      </ChartContainer>
    </div>
  );
}

// Export for convenience
export { PieChart as default };
